import {useDispatcher} from '@netx/core-components/hooks/useDispatcher';
import {type IAssetVersion} from '@netx/core-asset-version/schema';
import {CATEGORY_EVENT} from '@netx/core-category-next/actions/constants';
import {ASSET_BASE_EVENT} from '../actions/constants';
import type AssetModel from '@netx/core-asset/models/asset';
import type ConstituentClipModel from '@netx/core-asset/models/assetConstituent_clip';
import {useReactQueryGetAssetBean} from '../api/react-query';
import {type IAsset} from '../schema';
import {useQueryClient} from '@tanstack/react-query';
import {CART_ASSET_EVENT} from '@netx/core-cart-asset/actions/constants';
import {getAssetError} from '../errors';

// This is a hack to work around actions/jobs that modify the asset but we are not gettng the `moddate` updated before
// the action/job claims to be done
export const assetModifiedMap = new Map<number, number>();

export type UseAssetParams = {
	asset?: IAsset;
	id?: number;
};

export const useAsset = ({id = 0, asset}: UseAssetParams) => {
	const queryClient = useQueryClient();

	const query = useReactQueryGetAssetBean(
		{
			id,
		},
		{
			enabled: !!id,
		},
	);

	useDispatcher(
		'asset:versions:reactivate:succeeded:hack',
		(version: IAssetVersion) => {
			!!id && version.assetId === id && query.refetch();
		},
	);

	useDispatcher(
		'asset:constituent:clip:edit:success',
		(asset: AssetModel, clip: ConstituentClipModel, isNew = false) => {
			if (!id) return;
			if (asset.id === id && isNew) {
				query.refetch();
			}
		},
	);

	useDispatcher(
		[
			'asset:checkedOut',
			'asset:checkout:cancelled',
			'asset:locked',
			'asset:unlocked',
			'asset:task:complete:hack',
			'asset:version:upload:succeeded',
			'asset:custom:thumbnail:upload:succeeded',
			'asset:next:resynced',
			'asset:thumbnail:updated:hack',
			'asset:expiration:expire:success:hack',
			'asset:expiration:unexpire:success',
			'upload:asset:reimport:succeeded:hack',
		].join(' '),
		(assetId: number) => {
			if (!id || assetId !== id) return;
			query.refetch();
		},
	);

	useDispatcher(
		[
			'asset:attributes:bulk:edit:succeeded',
			'asset:tagPanel:grid:submit:succeeded',
			// when the asset detail is saved
			'asset:attributes:updated:hack',
			// we have to refetch to know if the items in the gallery were updated
			// this call can come from anywhere really
			'cart:addAll:succeeded',
		].join(' '),
		() => {
			if (!id) return;
			query.refetch();
		},
	);

	useDispatcher(
		'cart:delink:succeeded cart:link:succeeded',
		(cart: any, assetIds: number[]) => {
			if (!id || !assetIds.includes(id)) return;
			query.refetch();
		},
	);

	useDispatcher('asset:unexpire:success', (asset: AssetModel) => {
		if (!id || asset.id !== id) return;
		queryClient.setQueryData(['getAssetBean', {id}], (oldData) => {
			if (!oldData) return oldData;
			return {
				...oldData,
				expirationDate: 0,
			};
		});
	});

	useDispatcher(
		'asset:expire:success:hack',
		(_id: number, expirationDateStr: string, expirationDate: number) => {
			if (!id || _id !== id) return;
			queryClient.setQueryData(
				['getAssetBean', {id}],
				(oldData: IAsset | undefined) => {
					if (!oldData) return oldData;
					return {
						...oldData,
						expirationDate,
					};
				},
			);
		},
	);

	useDispatcher(
		`${ASSET_BASE_EVENT}:visibilityChanged`,
		(assetId: number, visible: boolean) => {
			if (!id || assetId !== id) return;
			queryClient.setQueryData(
				['getAssetBean', {id}],
				(oldData: IAsset | undefined) => {
					if (!oldData) return oldData;
					return {
						...oldData,
						visible: visible ? (1 as const) : (0 as const),
					};
				},
			);
		},
	);

	useDispatcher('cart:cleared', () => {
		if (!id || !query.data?.inCurrentCart) return;
		queryClient.setQueryData(
			['getAssetBean', {id}],
			(oldData: IAsset | undefined) => {
				if (!oldData) return oldData;
				return {
					...oldData,
					inCurrentCart: false,
				};
			},
		);
	});

	useDispatcher(CART_ASSET_EVENT.ADD_TO_CART, (assetId: number) => {
		if (!id || assetId !== id) return;
		queryClient.setQueryData(
			['getAssetBean', {id}],
			(oldData: IAsset | undefined) => {
				if (!oldData) return oldData;
				return {
					...oldData,
					inCurrentCart: true,
				};
			},
		);
	});

	useDispatcher(CART_ASSET_EVENT.REMOVE_FROM_CART, (assetId: number) => {
		if (!id || assetId !== id) return;
		// query.refetch();
		queryClient.setQueryData(
			['getAssetBean', {id}],
			(oldData: IAsset | undefined) => {
				if (!oldData) return oldData;
				return {
					...oldData,
					inCurrentCart: false,
				};
			},
		);
	});

	useDispatcher(CATEGORY_EVENT.ORGANIZE_ASSETS_SUCCESS, (params: any) => {
		if (!id) return;
		const asset = query.data;
		if (!asset) return;
		if (
			params.assetIds.includes(asset.assetId) ||
			(params.isCurrentCart && asset.inCurrentCart)
		) {
			query.refetch();
		}
	});

	return {
		...query,
		error: query.error ? getAssetError('read', query.error.code) : null,
		data: id ? query.data : asset,
		loading: id ? query.isLoading : false,
	};
};

export default useAsset;
